import { useCallback, useMemo } from "react";

import { useRecoilState } from "recoil";

import { MetricField } from "../../gql-generated/types";
import { formatNumbers } from "../../lens-stats/formatters";
import { type GetOrganicStatsQuery } from "../api/queries";
import { extractValueByIndex } from "../filters";
import { formatSubscribersDiffValue } from "../formatters";
import { SubscribersTotal } from "../models";
import { subscribersTotalAtom } from "../state";

import { useTimeRange } from "./useTimeRange";

export const useSubscribersTotal = (): {
    subscribersTotalValues: { totalValue: string; diffValue: string };
    setSubscribersTotalValues: (data: GetOrganicStatsQuery) => void;
} => {
    const { selectedTimeRange } = useTimeRange();
    const [subscribersTotal, setSubscribersTotal] = useRecoilState(subscribersTotalAtom);

    const setSubscribersTotalValues = useCallback(
        (data: GetOrganicStatsQuery) => {
            const [responseEntry] = data.getMyLensesOrganicStats.responseEntryList;

            const subscribersTotalPrevious = responseEntry
                ? extractValueByIndex(
                      responseEntry.statsResponseTimeEntryList,
                      responseEntry.statsResponseTimeEntryList.length / 2 - 1,
                      MetricField.Subscribers,
                  )
                : 0;

            const subscribersTotalCurrent = responseEntry
                ? extractValueByIndex(
                      responseEntry.statsResponseTimeEntryList,
                      responseEntry.statsResponseTimeEntryList.length - 1,
                      MetricField.Subscribers,
                  )
                : 0;

            setSubscribersTotal(
                new Map<SubscribersTotal, number>()
                    .set(SubscribersTotal.Previous, subscribersTotalPrevious)
                    .set(SubscribersTotal.Current, subscribersTotalCurrent),
            );
        },
        [setSubscribersTotal],
    );

    const subscribersTotalValues = useMemo(
        () => ({
            totalValue: formatNumbers(subscribersTotal.get(SubscribersTotal.Current) ?? 0),
            diffValue: formatSubscribersDiffValue(
                subscribersTotal.get(SubscribersTotal.Current) ?? 0,
                subscribersTotal.get(SubscribersTotal.Previous) ?? 0,
                selectedTimeRange,
            ),
        }),
        [subscribersTotal, selectedTimeRange],
    );

    return { setSubscribersTotalValues, subscribersTotalValues };
};
